import { memo, useCallback, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { AlertCircleIcon, Loading03Icon } from "@hugeicons/core-free-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCompanyTrend, useTrendActions } from "../hooks/useOrderAnalytics";
import { useOrderAnalyticsStore } from "../stores/orderAnalyticsStore";
import { TrendCharts } from "./TrendCharts";
import { TrendRanges } from "../types/orderAnalytics.types";
import type { TrendRange, TrendTarget } from "../types/orderAnalytics.types";

const RANGE_TABS: { value: TrendRange; label: string; unitLabel: string }[] = [
  { value: TrendRanges.WEEK, label: "1주일", unitLabel: "일 단위" },
  { value: TrendRanges.MONTH, label: "1개월", unitLabel: "일 단위" },
  { value: TrendRanges.QUARTER, label: "3개월", unitLabel: "주 단위" },
  { value: TrendRanges.YEAR, label: "1년", unitLabel: "월 단위" },
];

const TrendContent = memo(function TrendContent({
  target,
}: {
  target: TrendTarget;
}) {
  const [range, setRange] = useState<TrendRange>(TrendRanges.WEEK);
  const { data, error, isFetching } = useCompanyTrend(target.companyGuid, range);
  const { retryTrend } = useTrendActions();
  const activeTab = RANGE_TABS.find((t) => t.value === range);

  const handleRetry = useCallback(
    () => retryTrend(target.companyGuid, range),
    [range, retryTrend, target.companyGuid],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-sm">
          {target.companyName} · 기간별 경향
        </DialogTitle>
        <DialogDescription className="text-xs">
          상단 기간 설정과 무관하게 어제까지의 고정 기간으로 조회합니다.
          {data && ` (${data.startDate} ~ ${data.endDate}, ${activeTab?.unitLabel})`}
        </DialogDescription>
      </DialogHeader>

      <Tabs value={range} onValueChange={(value) => setRange(value as TrendRange)}>
        <TabsList>
          {RANGE_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="px-3 text-xs">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="max-h-[70vh] min-h-[320px] overflow-y-auto">
        {isFetching ? (
          <div className="flex h-[320px] flex-col items-center justify-center gap-2 text-xs text-gray-400">
            <HugeiconsIcon icon={Loading03Icon} size={20} className="animate-spin" />
            경향 데이터를 조회하는 중...
          </div>
        ) : error ? (
          <div className="flex h-[320px] flex-col items-center justify-center gap-2 text-center">
            <HugeiconsIcon icon={AlertCircleIcon} size={20} className="text-red-500" />
            <p className="text-xs text-red-600">경향 데이터를 불러오지 못했습니다</p>
            <p className="text-[11px] text-gray-400">{error.message}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="mt-1 h-7 rounded-md border border-gray-300 bg-white px-2.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              재시도
            </button>
          </div>
        ) : data ? (
          <TrendCharts trend={data} />
        ) : null}
      </div>
    </>
  );
});

export const TrendDialog = memo(function TrendDialog() {
  const target = useOrderAnalyticsStore((s) => s.trendTarget);
  const closeTrend = useOrderAnalyticsStore((s) => s.closeTrend);
  const { clearTrends } = useTrendActions();

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) return;
      closeTrend();
      clearTrends();
    },
    [clearTrends, closeTrend],
  );

  return (
    <Dialog open={target !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-3 p-5 sm:max-w-5xl">
        {target && <TrendContent key={target.companyGuid} target={target} />}
      </DialogContent>
    </Dialog>
  );
});
